import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  inject,
  signal,
  effect,
  input,
  output,
  computed
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmDynamicDialogService } from './confirm-dialog.service';
import type { ConfirmDialogVariant } from './confirm-dialog.types';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon';

/**
 * Confirm Dialog Component - Razor UI Design System
 * Drop-in replacement for Razor UI confirmDialog
 *
 * @example
 * <!-- Add once in your app, typically in app.component.html -->
 * <ui-confirm-dialog></ui-confirm-dialog>
 *
 * // Then use the service
 * confirmService.confirm({
 *   message: 'Delete this item?',
 *   header: 'Confirm',
 *   accept: () => this.delete()
 * });
 */
@Component({
  selector: 'ui-confirm-dialog',
  standalone: true,
  imports: [ButtonComponent, IconComponent],
  templateUrl: './confirm-dialog.component.html',
  styleUrl: './confirm-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConfirmDialogComponent implements OnInit, OnDestroy {
  /**
   * Keyboard-accept suppression window (ms) after a dialog is shown or
   * promoted from the service queue. A held Enter (key-repeat) or an
   * Enter/Space keyed at the PREVIOUS dialog must not resolve the newly
   * promoted one sight-unseen; within this window keyboard activation is
   * swallowed (preventDefault, no accept). Pointer clicks are unaffected.
   */
  private static readonly KEYBOARD_ACCEPT_SUPPRESSION_MS = 250;

  private readonly document = inject(DOCUMENT);
  private readonly confirmService = inject(ConfirmDynamicDialogService);

  /** Timestamp of the most recent service-driven dialog show/promotion. */
  private lastShownAt = 0;

  /** Optional key for filtering confirmations */
  readonly key = input<string | undefined>(undefined);

  /** Custom variant styling */
  readonly variant = input<ConfirmDialogVariant>('default');

  // Direct binding inputs for legacy-style template usage
  readonly headerInput = input<string | undefined>(undefined);
  readonly messageInput = input<string | undefined>(undefined);
  readonly iconInput = input<string | undefined>(undefined);
  readonly acceptLabelInput = input<string | undefined>(undefined);
  readonly rejectLabelInput = input<string | undefined>(undefined);
  readonly acceptSeverity = input<string | undefined>(undefined);

  // Output events for direct template binding (legacy-style)
  readonly acceptEvent = output<void>({ alias: 'onAccept' });
  readonly rejectEvent = output<void>({ alias: 'onReject' });
  readonly hideEvent = output<void>({ alias: 'onHide' });
  // GAP-FE-M009 round 9 · Aliased outputs for legacy templates
  // that bind (accept)/(reject)/(hide). Used by
  // tenant-branding-editor's reset/cancel dialogs (and any
  // future bound consumers that adopt the shorter event names).
  readonly acceptShortEvent = output<void>({ alias: 'accept' });
  readonly rejectShortEvent = output<void>({ alias: 'reject' });
  readonly hideShortEvent = output<void>({ alias: 'hide' });

  // Aliased inputs for direct binding (legacy library style: [header], [message], etc.)
  // These drive the internal writable signals via an effect (only when value is truthy).
  readonly headerPropInput = input<string | undefined>(undefined, { alias: 'header' });
  readonly messagePropInput = input<string | undefined>(undefined, { alias: 'message' });
  readonly iconPropInput = input<string | undefined>(undefined, { alias: 'icon' });
  readonly acceptLabelPropInput = input<string | undefined>(undefined, { alias: 'acceptLabel' });
  readonly rejectLabelPropInput = input<string | undefined>(undefined, { alias: 'rejectLabel' });

  // Aliased input for direct visible binding (legacy-style)
  readonly visibleInput = input<boolean | undefined>(undefined, { alias: 'visible' });

  // Reactive state from service
  private readonly serviceVisible = this.confirmService.visible;
  readonly confirmation = this.confirmService.confirmation;

  // Direct binding support for template usage (internal writable mirror of [visible])
  private readonly directVisible = signal(false);

  // GAP-FE-M009 round 8 · When the consumer binds [visible]
  // (i.e. uses the dialog declaratively as a presentation
  // component), do NOT also subscribe to the global
  // ConfirmationService — otherwise a service-driven confirm()
  // from elsewhere in the app would render this local dialog
  // too, leading to duplicate modal/backdrop and conflicting
  // labels. A dialog is "bound" when visibleInput() is ever
  // defined (true or false, not undefined).
  private readonly hasDirectBinding = computed(() => this.visibleInput() !== undefined);

  /** Check if visible (from service or direct input).
   *  Bound dialogs use only directVisible; service-driven
   *  dialogs (no [visible] input) use serviceVisible. */
  readonly isVisible = computed<boolean>(() =>
    this.hasDirectBinding() ? this.directVisible() : this.serviceVisible()
  );

  // Also expose as a function for template compatibility
  get visibleValue(): boolean {
    return this.hasDirectBinding() ? this.directVisible() : this.serviceVisible();
  }

  // Internal writable state — driven by confirmation() or aliased prop inputs
  readonly message = signal('');
  readonly header = signal('');
  readonly icon = signal('');
  readonly acceptLabel = signal('Yes');
  readonly rejectLabel = signal('No');
  readonly acceptVisible = signal(true);
  readonly rejectVisible = signal(true);
  readonly dismissableMask = signal(false);
  readonly closeOnEscape = signal(true);
  readonly defaultFocus = signal<'accept' | 'reject' | 'close' | 'none'>('accept');

  constructor() {
    // Stamp every service-driven show — the FIRST dialog AND each queue
    // promotion (requireConfirmation$ emits synchronously for both) — so
    // handleKeydown can suppress keyboard accept for a short window after
    // a new dialog appears (see KEYBOARD_ACCEPT_SUPPRESSION_MS).
    this.confirmService.requireConfirmation$
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.lastShownAt = Date.now();
      });

    // Sync from service confirmation — but only when this
    // dialog is NOT a directly-bound presentation component
    // (see GAP-FE-M009 round 8 note above).
    effect(() => {
      const conf = this.confirmation();
      if (conf && !this.hasDirectBinding()) {
        this.message.set(conf.message || '');
        this.header.set(conf.header || '');
        this.icon.set(conf.icon || '');
        this.acceptLabel.set(conf.acceptLabel || 'Yes');
        this.rejectLabel.set(conf.rejectLabel || 'No');
        this.acceptVisible.set(conf.acceptVisible !== false);
        this.rejectVisible.set(conf.rejectVisible !== false);
        this.dismissableMask.set(conf.dismissableMask || false);
        this.closeOnEscape.set(conf.closeOnEscape !== false);
        this.defaultFocus.set(conf.defaultFocus || 'accept');
      }
    });

    // Sync from legacy-style direct input props (only when truthy, preserving original setter semantics)
    effect(() => {
      const v = this.headerPropInput();
      if (v) { this.header.set(v); }
    });
    effect(() => {
      const v = this.messagePropInput();
      if (v) { this.message.set(v); }
    });
    effect(() => {
      const v = this.iconPropInput();
      if (v) { this.icon.set(v); }
    });
    effect(() => {
      const v = this.acceptLabelPropInput();
      if (v) { this.acceptLabel.set(v); }
    });
    effect(() => {
      const v = this.rejectLabelPropInput();
      if (v) { this.rejectLabel.set(v); }
    });

    // Sync [visible] input → internal directVisible signal
    effect(() => {
      const v = this.visibleInput();
      if (v !== undefined) { this.directVisible.set(v); }
    });
  }

  private readonly boundHandleKeydown = this.handleKeydown.bind(this);

  // GAP-FE-057 leak-audit: keydown listener is bound on the document.defaultView
  // in ngOnInit and detached symmetrically in ngOnDestroy. The bound function
  // reference is cached so add / remove operate on the same handle.
  ngOnInit(): void {
    // Listen for keyboard events
    const win = this.document.defaultView;
    if (win) {
      win.addEventListener('keydown', this.boundHandleKeydown);
    }
  }

  ngOnDestroy(): void {
    const win = this.document.defaultView;
    if (win) {
      win.removeEventListener('keydown', this.boundHandleKeydown);
    }
  }

  readonly dialogClasses = computed<string>(() => [
    'bg-bb-bg-secondary',
    'border',
    'border-bb-border',
    'rounded-xl',
    'shadow-2xl',
    'overflow-hidden'
  ].join(' '));

  readonly iconClasses = computed<string>(() => {
    const baseClasses = 'flex items-center justify-center w-8 h-8 rounded-full';
    const variantClasses = this.getVariantIconClasses();
    return `${baseClasses} ${variantClasses}`;
  });

  readonly largeIconClasses = computed<string>(() => {
    const baseClasses = 'flex items-center justify-center w-16 h-16 rounded-full text-2xl';
    const variantClasses = this.getVariantIconClasses();
    return `${baseClasses} ${variantClasses}`;
  });

  acceptVariant(): 'primary' | 'danger' | 'warning' {
    const icon = this.icon();
    const header = this.header().toLowerCase();

    // Detect danger/warning from icon or header
    if (icon.includes('trash') || icon.includes('delete') ||
        icon.includes('exclamation-triangle') || icon.includes('danger') ||
        header.includes('delete') || header.includes('remove')) {
      return 'danger';
    }
    if (icon.includes('warning') || header.includes('warning')) {
      return 'warning';
    }
    return 'primary';
  }

  private getVariantIconClasses(): string {
    const icon = this.icon();
    const header = this.header().toLowerCase();

    // Detect variant from icon or header
    if (icon.includes('trash') || icon.includes('delete') ||
        icon.includes('exclamation-triangle') || icon.includes('danger') ||
        header.includes('delete') || header.includes('remove')) {
      return 'bg-bb-accent-red/10 text-bb-accent-red';
    }
    if (icon.includes('warning') || header.includes('warning')) {
      return 'bg-bb-accent-amber/10 text-bb-accent-amber';
    }
    if (icon.includes('info') || icon.includes('question')) {
      return 'bg-bb-accent-blue/10 text-bb-accent-blue';
    }
    if (icon.includes('check') || icon.includes('success')) {
      return 'bg-bb-accent-green/10 text-bb-accent-green';
    }
    return 'bg-bb-accent-blue/10 text-bb-accent-blue';
  }

  onAccept(): void {
    // GAP-FE-M009 round 9 · Bound dialogs must not touch the
    // root ConfirmationService — otherwise a local click would
    // accept some unrelated global confirmation. Service-driven
    // dialogs (no [visible] binding) drive the service as before.
    if (this.hasDirectBinding()) {
      this.acceptEvent.emit();
      this.acceptShortEvent.emit();
      this.directVisible.set(false);
      return;
    }
    // Guard programmatic double-activation: a second onAccept() after the
    // service queue drained would emit accept events (and churn the
    // service) with nothing on screen.
    if (!this.serviceVisible()) {
      return;
    }
    this.confirmService.accept();
    this.acceptEvent.emit();
    this.acceptShortEvent.emit();
    this.directVisible.set(false);
  }

  onReject(): void {
    if (this.hasDirectBinding()) {
      this.rejectEvent.emit();
      this.rejectShortEvent.emit();
      this.hideEvent.emit();
      this.hideShortEvent.emit();
      this.directVisible.set(false);
      return;
    }
    // Guard programmatic double-activation (see onAccept).
    if (!this.serviceVisible()) {
      return;
    }
    this.confirmService.reject();
    this.rejectEvent.emit();
    this.rejectShortEvent.emit();
    this.hideEvent.emit();
    this.hideShortEvent.emit();
    this.directVisible.set(false);
  }

  onMaskClick(): void {
    if (!this.dismissableMask()) { return; }
    if (this.hasDirectBinding()) {
      this.hideEvent.emit();
      this.hideShortEvent.emit();
      this.directVisible.set(false);
      return;
    }
    this.confirmService.close();
    this.hideEvent.emit();
    this.hideShortEvent.emit();
    this.directVisible.set(false);
  }

  /**
   * True while keyboard activation must be swallowed: right after a dialog
   * is shown/promoted from the service queue. Direct-bound dialogs never
   * stamp `lastShownAt` (no queue), so the window never gates them.
   */
  private isInKeyboardAcceptSuppressionWindow(): boolean {
    return Date.now() - this.lastShownAt < ConfirmDialogComponent.KEYBOARD_ACCEPT_SUPPRESSION_MS;
  }

  private handleKeydown(event: KeyboardEvent): void {
    if (!this.isVisible()) {return;}

    if (event.key === 'Escape' && this.closeOnEscape()) {
      event.preventDefault();
      if (this.hasDirectBinding()) {
        this.rejectEvent.emit();
        this.rejectShortEvent.emit();
        this.hideEvent.emit();
        this.hideShortEvent.emit();
        this.directVisible.set(false);
        return;
      }
      this.confirmService.reject();
    }

    // Space activates the focused dialog button natively (keydown default
    // action → click). Within the post-promote suppression window, or when
    // the keystroke is an auto-repeat of a held key, swallow it so a Space
    // aimed at the previous dialog cannot activate the promoted one.
    if (event.key === ' ' && (event.repeat || this.isInKeyboardAcceptSuppressionWindow())) {
      event.preventDefault();
      return;
    }

    if (event.key === 'Enter' && this.defaultFocus() === 'accept') {
      // A held Enter auto-repeats; without this guard the repeat stream
      // crosses a queue promotion and accepts the next dialog sight-unseen.
      // preventDefault too — otherwise the focused button's native Enter
      // activation clicks it anyway.
      if (event.repeat) {
        event.preventDefault();
        return;
      }
      // Ignore keyboard accept for a short window after a dialog is
      // shown/promoted: an Enter keyed at the OLD dialog must not resolve
      // the NEW one. Pointer clicks are deliberately unaffected.
      if (this.isInKeyboardAcceptSuppressionWindow()) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      if (this.hasDirectBinding()) {
        this.acceptEvent.emit();
        this.acceptShortEvent.emit();
        this.directVisible.set(false);
        return;
      }
      this.confirmService.accept();
    }
  }
}
